import React, { memo, useContext } from 'react';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import { insert } from 'ramda';

import {
  IRapportUtdelning,
  IGrundurval,
} from '../../Queries/AnalysutdelningQuery/AnalysutdelningQuery';
import {
  ChangeVisibilityFnType,
  SetStartAnalysFnType,
  ChangeUrvalFnType,
  SetAvailableFnType,
} from '../AnalysUtdelningMain/AnalysUtdelningMain';
import EDTable from '../../../../../../../BaseComponents/Table/EDTable';
import EDTableRow from '../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EDTableCell from '../../../../../../../BaseComponents/Table/Components/EDTableCell';
import EDTableHead from '../../../../../../../BaseComponents/Table/Components/EDTableHead';
import EDTableBody from '../../../../../../../BaseComponents/Table/Components/EDTableBody';
import AnalysUtdelningTableRow from './Components/AnalysUtdelningTableRow/AnalysUtdelningTableRow';
import EDSortableTable, {
  ISortOrder,
} from '../../../../../../../BaseComponents/Table/Components/EDSortableTable';
import EDTableSortLabel from '../../../../../../../BaseComponents/Table/Components/EDTableSortLabel';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { numberOrStringCompare } from '../../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';

interface IProps {
  analyser: IRapportUtdelning[];
  setStartAnalys: SetStartAnalysFnType;
  changeUrval: ChangeUrvalFnType;
  changeVisibility: ChangeVisibilityFnType;
  urvalOptions: IGrundurval[];
  setAvailable: SetAvailableFnType;
  classes?: any;
}

const styles = (theme: Theme) => ({
  headerCell: {
    position: 'sticky',
    top: 0,
    zIndex: 120,
    backgroundColor: theme.palette.common.white,
  },
  tableBody: {
    zIndex: 90,
  },
  utdelningCell: {
    paddingRight: '20px',
  },
});

const categoryColName = 'Kategori';
const nameColName = 'Namn';
const availableColName = 'Tillgänglig i Ekonomisk analys';
const typeColName = 'Typ';
const combineWithColName = 'Kan kombineras med kodbehörighet';
const grundUrvalColName = 'Grundurval';
const visibleColName = 'Visa i analysmeny för nya användare';
const startColName = 'Använd som startanalys för nya användare';
const obligatoriskGasColName = 'Obligatorisk för rollerna';
const obligatoriskEjGasColName = 'Obligatorisk';

const tableHeaderTitles: string[] = [
  categoryColName,
  nameColName,
  availableColName,
  typeColName,
  combineWithColName,
  grundUrvalColName,
  visibleColName,
  startColName,
];

const sortableHeaders: string[] = [
  categoryColName,
  nameColName,
  typeColName,
  grundUrvalColName,
  obligatoriskGasColName,
  obligatoriskEjGasColName,
];

const getValue = (a: IRapportUtdelning, columnName: string) => {
  switch (columnName) {
    case categoryColName:
      return a.category;
    case nameColName:
      return a.name;
    case availableColName:
      return a.availableForAnalys ? 1 : -1;
    case typeColName:
      return a.type;
    case combineWithColName:
      return a.canCombineWith ? 1 : -1;
    case grundUrvalColName:
      return a.grundUrval ? a.grundUrval.name : '';
    case visibleColName:
      return a.defaultForNewUser ? 1 : -1;
    case startColName:
      return a.startAnalysForNewUser ? 1 : -1;
    case obligatoriskGasColName:
      return a.obligatoriskGas.map(role => role.name).join(', ');
    case obligatoriskEjGasColName:
      return a.obligatoriskEjGas ? 1 : -1;
    default:
      return null;
  }
};

const compareRapportUtdelning = (
  a: number | string,
  b: number | string,
  order: ISortOrder
) => {
  return numberOrStringCompare(a, b, order);
};

/**
 * Tabellvy för analysutdelningsrutinen
 */
export const AnalysUtdelningTable: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { analyser, setStartAnalys, changeVisibility, setAvailable } = props;

  const kodplanContext = useContext(KodplanContext);

  const obligatoriskHeaderTitle = kodplanContext.activeKodplan?.gas
    ? obligatoriskGasColName
    : obligatoriskEjGasColName;
  const tableHeaderTitlesWithObligatorisk = insert(
    4,
    obligatoriskHeaderTitle,
    tableHeaderTitles
  );

  return (
    <EDSortableTable
      sortInfo={{
        rows: analyser,
        properties: [
          { caption: '', order: 'asc', orderBy: categoryColName },
          { caption: '', order: 'asc', orderBy: nameColName },
        ],
      }}
      getValue={getValue}
      compareFunction={compareRapportUtdelning}
    >
      {({ sortInfo, createSortHandler }) => {
        return (
          <EDTable noBorderCollapse>
            <EDTableHead>
              <EDTableRow>
                {tableHeaderTitlesWithObligatorisk.map(title => {
                  const sortProperty = sortInfo.properties.find(
                    x => x.orderBy === title
                  );
                  const sortIndex =
                    sortInfo.properties.length > 1 && sortProperty
                      ? sortInfo.properties.indexOf(sortProperty) + 1
                      : undefined;

                  return (
                    <EDTableCell
                      key={title}
                      className={classNames(
                        props.classes.utdelningCell,
                        props.classes.headerCell
                      )}
                      onClick={createSortHandler(title, title)}
                    >
                      {sortableHeaders.includes(title) && (
                        <EDTableSortLabel
                          direction={
                            sortProperty ? sortProperty.order : undefined
                          }
                          sortIndex={sortIndex === 0 ? undefined : sortIndex}
                          active={Boolean(sortProperty)}
                        >
                          <EDTypography>{title}</EDTypography>
                        </EDTableSortLabel>
                      )}
                      {!sortableHeaders.includes(title) && (
                        <EDTypography>{title}</EDTypography>
                      )}
                    </EDTableCell>
                  );
                })}
              </EDTableRow>
            </EDTableHead>
            <EDTableBody className={props.classes.tableBody}>
              {sortInfo.rows.map(analys => (
                <AnalysUtdelningTableRow
                  key={analys.id}
                  analys={analys}
                  setStartAnalys={setStartAnalys}
                  changeVisibility={changeVisibility}
                  changeUrval={props.changeUrval}
                  urvalOptions={props.urvalOptions}
                  setAvailable={setAvailable}
                />
              ))}
            </EDTableBody>
          </EDTable>
        );
      }}
    </EDSortableTable>
  );
};

export default withStyles(memo(AnalysUtdelningTable), styles);
